/**
 * ZoomDialog - Modern product image zoom component v3.1 (Fixed Video & Navigation Bug)
 */

class ZoomDialog extends HTMLElement {
  constructor() {
    super();

    this._currentIndex = 0;
    this._totalSlides = 0;
    this._isZoomed = false;
    this._isOpen = false;

    // Desktop drag-pan state
    this._isDragging = false;
    this._dragStartX = 0;
    this._dragStartY = 0;
    this._currentPanX = 0;
    this._currentPanY = 0;
    this._wasDragging = false;

    // Touch / pinch state
    this._pinchStartDist = 0;
    this._pinchCurrentScale = 1;
    this._pinchBaseScale = 1;
    this._panX = 0;
    this._panY = 0;
    this._isPinching = false;
    this._lastTouchX = 0;
    this._lastTouchY = 0;
    this._isSwiping = false;
    this._swipeStartX = 0;

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
  }

  connectedCallback() {
    this._buildDOM();
    this._attachGalleryTriggers();
  }

  disconnectedCallback() {
    clearTimeout(this._closeTimer);
    cancelAnimationFrame(this._openFrame);
    cancelAnimationFrame(this._triggerFrame);
    this._triggerFrame = 0;

    this._galleryController?.abort();
    this._galleryObserver?.disconnect();
    this._boundsObserver?.disconnect();

    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);

    if (this._isOpen) {
      document.body.style.overflow = this._previousBodyOverflow ?? '';
    }

    const hadFocus = this._root?.contains(document.activeElement);

    this._isOpen = false;
    this._isDragging = false;

    if (this._img) {
      this._img.onload = null;
      this._img.onerror = null;
    }

    this._root?.remove();

    if (hadFocus && this._returnFocus?.isConnected) {
      this._returnFocus.focus({ preventScroll: true });
    }

    this._returnFocus = null;
    this._slides = [];
    this._totalSlides = 0;
  }


  _buildDOM() {
    this._backdrop = document.createElement('div');
    this._backdrop.className = 'mz-backdrop';
    this._backdrop.setAttribute('aria-hidden', 'true');
    this._backdrop.addEventListener('click', () => this.close());

    this._dialog = document.createElement('div');
    this._dialog.className = 'mz-dialog';
    this._dialog.setAttribute('role', 'dialog');
    this._dialog.setAttribute('aria-modal', 'true');
    this._dialog.setAttribute('aria-label', 'Product image zoom');

    // 工具栏：放大镜 + 关闭
    this._toolbar = document.createElement('div');
    this._toolbar.className = 'mz-toolbar';

    this._zoomBtn = document.createElement('button');
    this._zoomBtn.className = 'mz-tool-btn mz-tool-btn--zoom';
    this._zoomBtn.setAttribute('aria-label', 'Toggle zoom');
    this._zoomBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`;
    this._zoomBtn.addEventListener('click', (e) => { e.stopPropagation(); this._toggleZoomBtn(); });

    this._closeBtn = document.createElement('button');
    this._closeBtn.className = 'mz-tool-btn mz-tool-btn--close';
    this._closeBtn.setAttribute('aria-label', 'Close zoom view');
    this._closeBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    this._closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.close(); });

    this._toolbar.appendChild(this._zoomBtn);
    this._toolbar.appendChild(this._closeBtn);

    this._counter = document.createElement('div');
    this._counter.className = 'mz-counter';

    this._stage = document.createElement('div');
    this._stage.className = 'mz-stage';

    this._imageWrapper = document.createElement('div');
    this._imageWrapper.className = 'mz-image-wrapper';

    this._img = document.createElement('img');
    this._img.className = 'mz-image';
    this._img.setAttribute('draggable', 'false');

    this._imageWrapper.appendChild(this._img);
    this._stage.appendChild(this._imageWrapper);

    this._prevBtn = this._makeArrow('prev');
    this._nextBtn = this._makeArrow('next');

    this._dialog.appendChild(this._toolbar);
    this._dialog.appendChild(this._counter);
    this._dialog.appendChild(this._prevBtn);
    this._dialog.appendChild(this._stage);
    this._dialog.appendChild(this._nextBtn);

    this._root = document.createElement('div');
    this._root.className = 'mz-root';
    this._root.setAttribute('aria-hidden', 'true');
    this._root.appendChild(this._backdrop);
    this._root.appendChild(this._dialog);

    this._injectStyles();
    document.body.appendChild(this._root);

    this._stage.addEventListener('click', (e) => this._onStageClick(e));
    this._prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.prev(); });
    this._nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.next(); });

    this._imageWrapper.addEventListener('mousedown', (e) => this._onMouseDown(e));

    this._stage.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false });
    this._stage.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false });
    this._stage.addEventListener('touchend', (e) => this._onTouchEnd(e), { passive: false });
    this._stage.addEventListener('touchcancel', (e) => this._onTouchEnd(e), { passive: false });

    this._boundsObserver = new ResizeObserver(() => {
      if (!this._isOpen) return;

      this._isDragging = false;
      this._isPinching = false;
      this._isSwiping = false;
      this._touchMode = null;

      document.removeEventListener('mousemove', this._onMouseMove);
      document.removeEventListener('mouseup', this._onMouseUp);

      this._updateZoomButton();
      this._settlePan(false);
    });

    this._boundsObserver.observe(this._stage);
    this._boundsObserver.observe(this._dialog);
  }

  _makeArrow(direction) {
    const btn = document.createElement('button');
    btn.className = `mz-arrow mz-arrow--${direction}`;
    btn.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');
    btn.textContent = direction === 'prev' ? '‹' : '›';
    return btn;
  }

  // ─── Gallery Triggers ──────────────────────────────────────────────────────

  _attachGalleryTriggers() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    this._galleryController?.abort();
    this._galleryObserver?.disconnect();

    this._galleryController = new AbortController();
    const { signal } = this._galleryController;

    const schedule = () => {
      if (this._triggerFrame) return;

      this._triggerFrame = requestAnimationFrame(() => {
        this._triggerFrame = 0;
        if (this.isConnected) this._attachContainerTriggers();
      });
    };

    gallery.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.defaultPrevented) return;

      const container = event.target.closest('.product-media-container');
      if (!container || container.closest('media-gallery') !== gallery) return;
      if (!this._isImageContainer(container)) return;

      const magnifier = event.target.closest('.mz-magnifier');

      if (!magnifier) {
        if (!window.matchMedia('(pointer: fine)').matches) return;
        if (event.target.closest('button, a, input, select, textarea')) return;
        if (container.closest('slideshow-component[dragging]')) return;
      }

      event.preventDefault();
      event.stopPropagation();
      this._openByContainer(container);
    }, { signal });

    // 只观察当前图库，不再扫描整个页面。
    this._galleryObserver = new MutationObserver(schedule);
    this._galleryObserver.observe(gallery, {
      childList: true,
      subtree: true
    });

    schedule();
  }

  _isImageContainer(container) {
    return Boolean(container.querySelector('img')) &&
      !container.querySelector(
        'video, iframe, deferred-media, product-model, model-viewer, .deferred-media'
      );
  }

  _attachContainerTriggers() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    // 隐藏的桌面/移动布局也预先添加入口，切换布局后即可使用。
    gallery.querySelectorAll('.product-media-container').forEach((container) => {
      if (container.closest('media-gallery') !== gallery) return;
      if (!this._isImageContainer(container)) return;
      if (container.querySelector('.mz-magnifier')) return;

      container.style.position = 'relative';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mz-magnifier';
      button.setAttribute('aria-label', 'Zoom image');
      button.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round"
          width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21L16.65 16.65M11 8V14M8 11H14"/>
        </svg>
      `;

      container.appendChild(button);
    });
  }


  // 根据点击的真实图片容器来计算正确的索引值
  _openByContainer(targetContainer) {
    this._collectSlides();
    if (this._totalSlides === 0) return;

    // 核心修复点 2：在过滤后的纯图片列表中，精确查找当前容器的索引位置
    const index = this._slides.findIndex(slide => slide.element === targetContainer);
    if (index !== -1) {
      this.open(index);
    }
  }

  open(index = 0) {
    if (!this.isConnected || !this._root?.isConnected) return;

    this._collectSlides();
    if (this._totalSlides === 0) return;

    clearTimeout(this._closeTimer);
    cancelAnimationFrame(this._openFrame);

    if (!this._isOpen) {
      this._previousBodyOverflow = document.body.style.overflow;
      this._returnFocus = document.activeElement;
    }

    this._isOpen = true;
    this._currentIndex = Math.max(0, Math.min(index, this._totalSlides - 1));
    this._resetZoom();
    this._loadSlide(this._currentIndex);
    this._updateArrows();
    this._updateCounter();

    this._root.setAttribute('aria-hidden', 'false');
    this._root.classList.add('mz-root--open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeyDown);

    this._openFrame = requestAnimationFrame(() => {
      if (!this.isConnected || !this._isOpen) return;
      this._root.classList.add('mz-root--visible');
      this._closeBtn.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this._isOpen) return;

    this._isOpen = false;
    this._isDragging = false;

    clearTimeout(this._closeTimer);
    cancelAnimationFrame(this._openFrame);

    this._root.classList.remove('mz-root--visible');
    document.body.style.overflow = this._previousBodyOverflow ?? '';

    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);

    if (this._returnFocus?.isConnected) {
      this._returnFocus.focus({ preventScroll: true });
    }
    this._returnFocus = null;

    this._closeTimer = setTimeout(() => {
      if (!this.isConnected || this._isOpen) return;

      this._root.classList.remove('mz-root--open');
      this._root.setAttribute('aria-hidden', 'true');
      this._resetZoom();
    }, 280);
  }

  prev() {
    this._currentIndex = (this._currentIndex - 1 + this._totalSlides) % this._totalSlides;
    this._resetZoom();
    this._loadSlide(this._currentIndex);
    this._updateArrows();
    this._updateCounter();
  }

  next() {
    this._currentIndex = (this._currentIndex + 1) % this._totalSlides;
    this._resetZoom();
    this._loadSlide(this._currentIndex);
    this._updateArrows();
    this._updateCounter();
  }

  // ─── Slide Management ──────────────────────────────────────────────────────

  _collectSlides() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    const containers = Array.from(gallery.querySelectorAll('.product-media-container'))
      .filter(c => c.offsetParent !== null && c.getClientRects().length);

    this._slides = containers
      .filter(container => {
        // 核心修复点 3：严苛的过滤机制，如果容器内包含视频或者 3D 模型，直接从列表中剔除
        if (container.querySelector('video, deferred-media, model-viewer, .deferred-media')) {
          return false;
        }
        return true;
      })
      .map((container) => {
        const img = container.querySelector('img');
        return {
          src: img ? (img.dataset.src || img.src) : '',
          srcset: img ? (img.dataset.srcset || img.srcset) : '',
          alt: img ? img.alt : '',
          element: container,
        };
      })
      .filter(s => s.src);

    this._totalSlides = this._slides.length;
  }

  _loadSlide(index) {
    const slide = this._slides[index];
    if (!slide) return;

    // 清除上一次加载的回调。
    this._img.onload = null;
    this._img.onerror = null;

    if (!this._imageRetry || !this._stage.contains(this._imageRetry)) {
      this._imageRetry = document.createElement('button');
      this._imageRetry.type = 'button';
      this._imageRetry.textContent = 'Image could not load. Tap to retry.';
      this._imageRetry.style.cssText = `
        position: absolute;
        z-index: 5;
        max-width: 85%;
        padding: 12px 16px;
        border: 1px solid #ffffff;
        border-radius: 4px;
        background: #1a1a1a;
        color: #ffffff;
        font: inherit;
        cursor: pointer;
      `;

      this._imageRetry.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!this._isOpen || !this.isConnected) return;
        this._loadSlide(this._currentIndex);
      });

      this._stage.appendChild(this._imageRetry);
    }

    this._imageRetry.hidden = true;
    this._img.style.opacity = '0';
    this._img.style.transform = 'scale(0.96)';

    let src = slide.src;

    if (src.includes('cdn.shopify.com')) {
      src = src.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|\d*x\d+)(@\dx)?(\.[a-z]+)(\?|$)/,
        '$3$4'
      );

      if (!src.includes('&width=') && !src.includes('?width=')) {
        src += (src.includes('?') ? '&' : '?') + 'width=2048';
      }
    }

    const onLoaded = () => {
      if (!this.isConnected) return;

      this._imageRetry.hidden = true;
      this._img.style.transition =
        'opacity 0.2s ease, transform 0.2s ease';
      this._img.style.opacity = '1';
      this._img.style.transform = 'scale(1)';
      if (this._isOpen) this._settlePan(false);
    };

    this._img.onload = onLoaded;

    this._img.onerror = () => {
      if (!this.isConnected) return;

      this._img.style.opacity = '0';
      this._imageRetry.hidden = false;
    };

    this._img.alt = slide.alt || '';

    // 清除上一张图的候选地址，避免沿用旧 srcset。
    if (slide.srcset) {
      this._img.srcset = slide.srcset;
    } else {
      this._img.removeAttribute('srcset');
    }

    this._img.src = src;

    if (this._img.complete && this._img.naturalWidth > 0) {
      onLoaded();
    }
  }


    _onStageClick(e) {
    if (this._wasDragging) {
      this._wasDragging = false;
      return;
    }

    if (this._isPinching) return;

    if (e.target === this._stage) {
      this.close();
      return;
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this._toggleZoomBtn();
    }
  }

  // 计算 object-fit: contain 下，图片实际占据的尺寸。
  _getPanLimits() {
    const boxWidth = this._img.clientWidth;
    const boxHeight = this._img.clientHeight;
    const naturalWidth = this._img.naturalWidth;
    const naturalHeight = this._img.naturalHeight;

    if (!boxWidth || !boxHeight || !naturalWidth || !naturalHeight) {
      return { x: 0, y: 0 };
    }

    const fit = Math.min(
      boxWidth / naturalWidth,
      boxHeight / naturalHeight
    );

    const scale = this._pinchCurrentScale || 1;
    const viewportWidth = this._dialog.clientWidth;
    const viewportHeight = this._dialog.clientHeight;

    return {
      x: Math.max(0, (naturalWidth * fit * scale - viewportWidth) / 2),
      y: Math.max(0, (naturalHeight * fit * scale - viewportHeight) / 2)
    };
  }

  // 到边缘后增加阻力，最大只允许轻微越界。
  _resistPan(value, limit) {
    if (limit <= 0) return 0;

    const distance = Math.abs(value);
    if (distance <= limit) return value;

    const excess = distance - limit;
    const resistance = 64 * excess / (excess + 160);

    return Math.sign(value) * (limit + resistance);
  }

  _renderPan(animated = false) {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this._imageWrapper.style.transition =
      animated && !reducedMotion
        ? 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';

    // 平移统一用屏幕像素，避免不同缩放倍数下拖动距离不一致。
    this._imageWrapper.style.transform =
      `translate3d(${this._currentPanX}px, ${this._currentPanY}px, 0) ` +
      `scale(${this._pinchCurrentScale})`;
  }

  _settlePan(animated = true) {
    const limits = this._getPanLimits();

    this._currentPanX = Math.max(
      -limits.x,
      Math.min(limits.x, this._currentPanX)
    );
    this._currentPanY = Math.max(
      -limits.y,
      Math.min(limits.y, this._currentPanY)
    );

    this._renderPan(animated);
  }

  _updateZoomButton() {
    this._isZoomed = this._pinchCurrentScale > 1;
    this._zoomBtn.setAttribute(
      'aria-label',
      this._isZoomed ? 'Zoom out' : 'Zoom in'
    );

    this._zoomBtn.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"
        width="20" height="20" aria-hidden="true">
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21L16.65 16.65M8 11H14"/>
        ${this._isZoomed ? '' : '<path d="M11 8V14"/>'}
      </svg>
    `;

    this._imageWrapper.style.cursor = this._isZoomed ? 'grab' : '';
  }

  _toggleZoomBtn() {
    if (this._pinchCurrentScale > 1) {
      this._resetZoom();
      return;
    }

    this._pinchCurrentScale =
      window.matchMedia('(pointer: fine)').matches ? 2.5 : 2;

    this._currentPanX = 0;
    this._currentPanY = 0;
    this._updateZoomButton();
    this._settlePan(true);
  }

  _onMouseDown(e) {
    if (this._pinchCurrentScale <= 1 || e.button !== 0) return;

    e.preventDefault();
    this._settlePan(false);

    this._isDragging = true;
    this._wasDragging = false;
    this._mouseStartX = e.clientX;
    this._mouseStartY = e.clientY;
    this._dragStartPanX = this._currentPanX;
    this._dragStartPanY = this._currentPanY;
    this._dragLimits = this._getPanLimits();

    this._imageWrapper.style.cursor = 'grabbing';

    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
  }

  _onMouseMove(e) {
    if (!this._isDragging) return;

    const dx = e.clientX - this._mouseStartX;
    const dy = e.clientY - this._mouseStartY;

    if (Math.abs(dx) + Math.abs(dy) > 3) {
      this._wasDragging = true;
    }

    this._currentPanX = this._resistPan(
      this._dragStartPanX + dx,
      this._dragLimits.x
    );
    this._currentPanY = this._resistPan(
      this._dragStartPanY + dy,
      this._dragLimits.y
    );

    this._renderPan(false);
  }

  _onMouseUp() {
    if (!this._isDragging) return;

    this._isDragging = false;
    this._imageWrapper.style.cursor = 'grab';

    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);

    this._settlePan(true);
  }

  _resetZoom() {
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);

    this._isDragging = false;
    this._wasDragging = false;
    this._isPinching = false;
    this._isSwiping = false;
    this._touchMode = null;

    this._pinchCurrentScale = 1;
    this._currentPanX = 0;
    this._currentPanY = 0;

    this._updateZoomButton();
    this._renderPan(true);
  }

  _getTouchDist(touches) {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );
  }

  _startSingleTouch(touch) {
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
    this._touchStartPanX = this._currentPanX;
    this._touchStartPanY = this._currentPanY;
    this._touchMode = this._pinchCurrentScale > 1 ? 'pan' : 'swipe';
    this._dragLimits = this._getPanLimits();
    this._isSwiping = false;
  }

  _onTouchStart(e) {
    this._wasDragging = false;
    this._settlePan(false);

    if (e.touches.length >= 2) {
      e.preventDefault();
      this._isPinching = true;
      this._touchMode = 'pinch';
      this._pinchStartDist = Math.max(1, this._getTouchDist(e.touches));
      this._pinchBaseScale = this._pinchCurrentScale;
      this._pinchStartPanX = this._currentPanX;
      this._pinchStartPanY = this._currentPanY;
    } else if (e.touches.length === 1) {
      this._isPinching = false;
      this._startSingleTouch(e.touches[0]);
    }
  }

  _onTouchMove(e) {
    if (e.touches.length >= 2 && this._isPinching) {
      e.preventDefault();
      this._wasDragging = true;

      this._pinchCurrentScale = Math.max(
        1,
        Math.min(
          4,
          this._pinchBaseScale *
            this._getTouchDist(e.touches) / this._pinchStartDist
        )
      );

      const ratio = this._pinchCurrentScale / this._pinchBaseScale;
      const limits = this._getPanLimits();

      this._currentPanX = this._resistPan(
        this._pinchStartPanX * ratio,
        limits.x
      );
      this._currentPanY = this._resistPan(
        this._pinchStartPanY * ratio,
        limits.y
      );

      this._renderPan(false);
      return;
    }

    if (e.touches.length !== 1 || this._isPinching) return;

    const dx = e.touches[0].clientX - this._touchStartX;
    const dy = e.touches[0].clientY - this._touchStartY;

    if (this._touchMode === 'pan') {
      e.preventDefault();

      if (Math.abs(dx) + Math.abs(dy) > 3) {
        this._wasDragging = true;
      }

      this._currentPanX = this._resistPan(
        this._touchStartPanX + dx,
        this._dragLimits.x
      );
      this._currentPanY = this._resistPan(
        this._touchStartPanY + dy,
        this._dragLimits.y
      );

      this._renderPan(false);
    } else if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      e.preventDefault();
      this._isSwiping = true;
      this._wasDragging = true;
    }
  }

  _onTouchEnd(e) {
    if (e.type === 'touchcancel') {
      this._isPinching = false;
      this._isSwiping = false;
      this._touchMode = null;
      this._updateZoomButton();
      this._settlePan(true);
      return;
    }

    if (this._isPinching && e.touches.length < 2) {
      this._isPinching = false;

      if (this._pinchCurrentScale < 1.1) {
        this._pinchCurrentScale = 1;
      }

      this._updateZoomButton();
      this._settlePan(true);

      if (e.touches.length === 1) {
        this._startSingleTouch(e.touches[0]);
      } else {
        this._touchMode = null;
      }
      return;
    }

    if (e.touches.length) return;

    if (
      this._touchMode === 'swipe' &&
      this._isSwiping &&
      e.changedTouches.length
    ) {
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      if (Math.abs(dx) > 50) {
        if (dx < 0) this.next();
        else this.prev();
        this._wasDragging = true;
      }
    } else {
      this._settlePan(true);
    }

    this._isSwiping = false;
    this._touchMode = null;
  }


  _updateArrows() {
    const visible = this._totalSlides > 1;
    this._prevBtn.style.display = visible ? '' : 'none';
    this._nextBtn.style.display = visible ? '' : 'none';
  }

  _updateCounter() {
    if (this._totalSlides > 1) {
      this._counter.textContent = `${this._currentIndex + 1} / ${this._totalSlides}`;
      this._counter.style.display = 'block';
    } else {
      this._counter.style.display = 'none';
    }
  }

  _onKeyDown(e) {
    if (!this._isOpen) return;

    if (e.key === 'Tab') {
      const buttons = Array.from(
        this._dialog.querySelectorAll('button:not([disabled])')
      ).filter((button) =>
        !button.hidden &&
        button.tabIndex >= 0 &&
        button.getClientRects().length > 0
      );

      if (!buttons.length) {
        e.preventDefault();
        return;
      }

      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const active = document.activeElement;
      const inside = buttons.includes(active);

      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }

      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  }

  // ─── Styles ────────────────────────────────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('mz-styles')) return;
    const style = document.createElement('style');
    style.id = 'mz-styles';
    style.textContent = `
      .mz-root {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 9999;
      }
      .mz-root--open { display: block; }

      .mz-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,0.88);
        opacity: 0;
        transition: opacity 0.28s ease;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
      }
      .mz-root--visible .mz-backdrop { opacity: 1; }

      .mz-dialog {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }

      .mz-stage {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: min(80vmin, 800px);
        height: min(80vmin, 800px);
        pointer-events: all;
        cursor: zoom-in;
        opacity: 0;
        transform: scale(0.97);
        transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1);
        overflow: visible;
      }
      .mz-root--visible .mz-stage {
        opacity: 1;
        transform: scale(1);
      }

      .mz-image-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        transform-origin: center center;
        will-change: transform;
        touch-action: none;
      }

      .mz-image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        user-select: none;
        -webkit-user-drag: none;
      }

      /* 磨砂玻璃风格工具栏 */
.mz-toolbar {
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  z-index: 10;
  pointer-events: all;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 4px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  opacity: 0;
  transform: translateY(-8px);
  transition: opacity 0.28s ease 0.1s, transform 0.28s ease 0.1s;
}
      .mz-root--visible .mz-toolbar { opacity: 1; transform: translateY(0); }

      .mz-tool-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        color: #fff;
        cursor: pointer;
        border-radius: 6px;
        transition: background 0.15s ease;
        flex-shrink: 0;
      }
      .mz-tool-btn:hover { background: rgba(255, 255, 255, 0.2); }

      .mz-counter {
        position: fixed;
        top: 24px;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255,255,255,0.9);
text-shadow: 0 1px 4px rgba(0,0,0,0.5);
        font-size: 13px;
        font-family: inherit;
        letter-spacing: 0.5px;
        pointer-events: none;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.28s ease 0.1s;
      }
      .mz-root--visible .mz-counter { opacity: 1; }

      .mz-arrow {
        position: fixed;
        top: 50%;
        transform: translateY(-50%);
        border: none;
        background: transparent;
        color: #fff;
        cursor: pointer;
        pointer-events: all;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.28s ease 0.1s;
        font-size: 72px;
        font-weight: 200;
        line-height: 1;
        padding: 0 20px;
        text-shadow: 0 0 12px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4);
        user-select: none;
        -webkit-user-select: none;
        width: 80px;
        display: flex;
        align-items: center;
      }
      .mz-root--visible .mz-arrow { opacity: 0.8; }
      .mz-arrow--prev { left: 0; justify-content: flex-start; }
      .mz-arrow--next { right: 0; justify-content: flex-end; }
      .mz-arrow:hover { opacity: 1 !important; }

      @media (pointer: coarse) {
        .mz-arrow {
          font-size: 56px;
          padding: 0 14px;
          width: 64px;
        }
      }

      /* 还原主图上的移动端放大镜UI样式 */
      .mz-magnifier {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.08);
        background: #ffffff;
        color: #333333;
        cursor: pointer;
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 4;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        transition: background 0.15s ease, transform 0.15s ease;
      }
      @media (pointer: coarse) {
        .mz-magnifier { display: flex; }
      }

      @media (pointer: fine) {
        .mz-magnifier {
          display: flex;
          opacity: 0;
        }

        .mz-magnifier:focus-visible {
          opacity: 1;
          outline: 2px solid #1a1a1a;
          outline-offset: 3px;
        }
      }
      .mz-magnifier:active { background: #f5f5f5; transform: scale(0.94); }

      @media (pointer: coarse) {
        .mz-stage {
          width: 100vw;
          height: 100dvh;
          cursor: default;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}